import { useState } from "react";
import { Building2 } from "lucide-react";
import type { Brand } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchableSelect } from "@/lib/picker-search";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonaWorkflowConfigCard } from "@/components/persona-workflow-config-card";
import { CustomerEvidenceConfigCard } from "@/components/customer-evidence-config-card";

type PersonaWorkflowsConfigTabProps = {
  allBrands: Brand[];
  isLoading?: boolean;
};

export function PersonaWorkflowsConfigTab({
  allBrands,
  isLoading = false,
}: PersonaWorkflowsConfigTabProps) {
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const selectedBrand = allBrands.find((brand) => brand.id === selectedBrandId) ?? allBrands[0] ?? null;

  return (
    <div className="max-w-5xl space-y-6" data-testid="persona-workflows-config-tab">
      <header>
        <h2 className="text-2xl font-semibold">Persona Workflows</h2>
        <p className="text-sm text-muted-foreground">
           Configure persona workflows and customer evidence access for each brand.
        </p>
      </header>

      {isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Loading brands</CardTitle>
            <CardDescription>Preparing persona workflow settings.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3" aria-label="Loading persona workflow brands">
            <Skeleton className="h-10 w-full sm:w-80" />
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      ) : allBrands.length === 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">No brands available</CardTitle>
                <CardDescription>
                  Add a brand in Admin to configure its persona workflows.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Choose a brand</CardTitle>
              <CardDescription>
                 Workflow and evidence settings are saved independently for each brand.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SearchableSelect
                value={selectedBrand?.id ?? ""}
                onValueChange={setSelectedBrandId}
                triggerClassName="w-full sm:w-[320px]"
                testId="select-persona-workflows-brand"
                ariaLabel="Choose a brand"
                placeholder="Select a brand"
                alwaysShowSearch
                selectedDisplay={selectedBrand?.name}
                options={allBrands.map((brand) => ({
                  value: brand.id,
                  label: brand.name,
                }))}
              />
            </CardContent>
          </Card>
          {selectedBrand && (
            <>
              <PersonaWorkflowConfigCard key={selectedBrand.id} brand={selectedBrand} />
              <CustomerEvidenceConfigCard key={`evidence-${selectedBrand.id}`} brand={selectedBrand} />
            </>
          )}
        </>
      )}
    </div>
  );
}
